import { useEffect, useMemo, useState } from 'react';
import {
  platformApi,
  platformSession,
  type PlatformAlertSettings,
  type PlatformBusinessType,
  type PlatformStorageSettings,
  type UpdatePlatformStorageSettingsPayload,
} from '@/core/api/platformApi';
import type { TenantAlertSettings } from '@/core/types/tenantWorkspace';
import { getErrorMessage } from '@/core/utils/errors';
import { AppIcon } from '@/shared/components/AppIcon';

const alertDefaults: TenantAlertSettings = {
  isEnabled: true,
  smsCreditWarningEnabled: true,
  smsRemainingThreshold: 20,
  staffWarningEnabled: false,
  staffCountThreshold: 0,
  licenseWarningEnabled: true,
  licenseDaysBeforeExpiry: 7,
};

type StorageForm = Pick<PlatformStorageSettings, 'provider' | 'endpoint' | 'region' | 'bucketName' | 'publicBaseUrl' | 'prefix' | 'publicRead'>;

const storageDefaults: StorageForm = {
  provider: 'Local',
  endpoint: 'https://s3.ir-thr-at1.arvanstorage.ir',
  region: 'ir-thr-at1',
  bucketName: '',
  publicBaseUrl: '',
  prefix: 'crm/catalog',
  publicRead: true,
};

const storageFromResponse = (settings: PlatformStorageSettings): StorageForm => ({
  provider: settings.provider,
  endpoint: settings.endpoint,
  region: settings.region,
  bucketName: settings.bucketName,
  publicBaseUrl: settings.publicBaseUrl,
  prefix: settings.prefix,
  publicRead: settings.publicRead,
});

const alertDraft = (settings: TenantAlertSettings): TenantAlertSettings => ({
  isEnabled: settings.isEnabled,
  smsCreditWarningEnabled: settings.smsCreditWarningEnabled,
  smsRemainingThreshold: settings.smsRemainingThreshold,
  staffWarningEnabled: settings.staffWarningEnabled,
  staffCountThreshold: settings.staffCountThreshold,
  licenseWarningEnabled: settings.licenseWarningEnabled,
  licenseDaysBeforeExpiry: settings.licenseDaysBeforeExpiry,
});

const fieldClass = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100';

export const PlatformSettingsPage = (): React.ReactElement => {
  const canManageAlerts = platformSession.hasPermission('platform.alerts.manage');
  const canManageStorage = platformSession.hasPermission('platform.storage.manage');
  const [alertSettings, setAlertSettings] = useState<TenantAlertSettings>(alertDefaults);
  const [globalAlertSettings, setGlobalAlertSettings] = useState<TenantAlertSettings>(alertDefaults);
  const [alertProfiles, setAlertProfiles] = useState<PlatformAlertSettings['businessTypeProfiles']>([]);
  const [businessTypes, setBusinessTypes] = useState<PlatformBusinessType[]>([]);
  const [alertScope, setAlertScope] = useState<string>('global');
  const [storage, setStorage] = useState<StorageForm>(storageDefaults);
  const [hasAccessKey, setHasAccessKey] = useState(false);
  const [hasSecretKey, setHasSecretKey] = useState(false);
  const [storageConfigured, setStorageConfigured] = useState(true);
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [clearAccessKey, setClearAccessKey] = useState(false);
  const [clearSecretKey, setClearSecretKey] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(canManageAlerts);
  const [storageLoading, setStorageLoading] = useState(canManageStorage);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [savingStorage, setSavingStorage] = useState(false);
  const [testingStorage, setTestingStorage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const selectedBusinessType = useMemo(
    () => businessTypes.find((item) => item.id === alertScope) ?? null,
    [alertScope, businessTypes],
  );
  const selectedProfile = useMemo(
    () => alertProfiles.find((item) => item.businessTypeId === alertScope) ?? null,
    [alertProfiles, alertScope],
  );

  const applyAlertResponse = (result: PlatformAlertSettings, scope = alertScope): void => {
    const defaults = alertDraft(result);
    setGlobalAlertSettings(defaults);
    setAlertProfiles(result.businessTypeProfiles);
    if (scope === 'global') {
      setAlertSettings(defaults);
      return;
    }
    const profile = result.businessTypeProfiles.find((item) => item.businessTypeId === scope);
    setAlertSettings(alertDraft(profile ?? defaults));
  };

  useEffect(() => {
    const load = async (): Promise<void> => {
      const operations: Promise<void>[] = [];
      if (canManageAlerts) {
        operations.push(Promise.all([platformApi.alertSettings(), platformApi.listBusinessTypes()])
          .then(([settings, types]) => {
            setBusinessTypes(types);
            applyAlertResponse(settings, 'global');
          })
          .finally(() => setAlertsLoading(false)));
      }
      if (canManageStorage) {
        operations.push(platformApi.storageSettings()
          .then((result) => {
            setStorage(storageFromResponse(result));
            setHasAccessKey(result.hasAccessKey);
            setHasSecretKey(result.hasSecretKey);
            setStorageConfigured(result.isConfigured);
          })
          .finally(() => setStorageLoading(false)));
      }
      try {
        await Promise.all(operations);
      } catch (loadError) {
        setError(getErrorMessage(loadError));
      }
    };
    void load();
  }, [canManageAlerts, canManageStorage]);

  const updateAlert = <Key extends keyof TenantAlertSettings>(key: Key, value: TenantAlertSettings[Key]): void =>
    setAlertSettings((current) => ({ ...current, [key]: value }));
  const updateStorage = <Key extends keyof StorageForm>(key: Key, value: StorageForm[Key]): void =>
    setStorage((current) => ({ ...current, [key]: value }));

  const storagePayload = (): UpdatePlatformStorageSettingsPayload => ({
    ...storage,
    accessKey: accessKey || null,
    secretKey: secretKey || null,
    clearAccessKey,
    clearSecretKey,
  });

  const saveAlerts = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSavingAlerts(true);
    setError(null);
    setNotice(null);
    try {
      const result = await platformApi.updateAlertSettings({
        ...alertSettings,
        businessTypeId: alertScope === 'global' ? null : alertScope,
      });
      applyAlertResponse(result);
      setNotice(alertScope === 'global'
        ? 'تنظیمات پیش‌فرض هشدار برای همه شرکت‌ها ذخیره شد.'
        : `تنظیم هشدار اختصاصی برای «${selectedBusinessType?.name ?? 'نوع کسب‌وکار'}» و قالب پیشنهادی آن ذخیره شد.`);
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSavingAlerts(false);
    }
  };

  const selectAlertScope = (scope: string): void => {
    setAlertScope(scope);
    const profile = alertProfiles.find((item) => item.businessTypeId === scope);
    setAlertSettings(alertDraft(profile ?? globalAlertSettings));
    setError(null);
    setNotice(null);
  };

  const resetBusinessTypeAlerts = async (): Promise<void> => {
    if (alertScope === 'global') return;
    setSavingAlerts(true);
    setError(null);
    setNotice(null);
    try {
      const result = await platformApi.updateAlertSettings({
        ...alertSettings,
        businessTypeId: alertScope,
        resetToGlobal: true,
      });
      applyAlertResponse(result);
      setNotice(`هشدارهای «${selectedBusinessType?.name ?? 'این نوع کسب‌وکار'}» دوباره از تنظیم پیش‌فرض استفاده می‌کنند.`);
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSavingAlerts(false);
    }
  };

  const saveStorage = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSavingStorage(true);
    setError(null);
    setNotice(null);
    try {
      const result = await platformApi.updateStorageSettings(storagePayload());
      setStorage(storageFromResponse(result));
      setHasAccessKey(result.hasAccessKey);
      setHasSecretKey(result.hasSecretKey);
      setStorageConfigured(result.isConfigured);
      setAccessKey('');
      setSecretKey('');
      setClearAccessKey(false);
      setClearSecretKey(false);
      setNotice(result.provider === 'Arvan'
        ? 'اتصال فضای ذخیره‌سازی آروان‌کلاد ذخیره شد و از این پس آپلود کاتالوگ‌ها از آن استفاده می‌کند.'
        : 'فضای ذخیره‌سازی محلی برای آپلود کاتالوگ‌ها فعال شد.');
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    } finally {
      setSavingStorage(false);
    }
  };

  const testStorageConnection = async (): Promise<void> => {
    setTestingStorage(true);
    setError(null);
    setNotice(null);
    try {
      const result = await platformApi.testStorageConnection(storagePayload());
      const elapsed = result.elapsedMilliseconds > 0 ? ` (${result.elapsedMilliseconds.toLocaleString('fa-IR')} میلی‌ثانیه)` : '';
      if (result.isSuccessful) {
        setNotice(`${result.message}${elapsed}`);
      } else {
        setError(`${result.message}${elapsed}`);
      }
    } catch (testError) {
      setError(getErrorMessage(testError));
    } finally {
      setTestingStorage(false);
    }
  };

  return <main className="min-h-[calc(100vh-74px)] text-slate-900" dir="rtl">
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-7">
      <section>
        <p className="text-sm font-bold text-brand-600">تنظیمات مرکزی پلتفرم</p>
        <h1 className="mt-1 text-2xl font-black text-ink-950 sm:text-3xl">تنظیمات سراسری</h1>
        <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-500">هشدارها و محل نگه‌داری فایل‌های کاتالوگ را یک‌بار در اینجا تنظیم کنید. این تنظیمات برای همه شرکت‌ها اعمال می‌شوند.</p>
      </section>

      {error ? <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">{error}</div> : null}
      {notice ? <div className="flex items-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700"><AppIcon name="check" size={17} />{notice}</div> : null}

      {canManageStorage ? <form className="overflow-hidden rounded-3xl border border-sky-100 bg-white shadow-sm" onSubmit={(event) => void saveStorage(event)}>
        <div className="flex flex-col gap-4 border-b border-sky-100 bg-sky-50/40 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-7">
          <div className="flex items-start gap-3"><span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-sky-100 text-sky-700"><AppIcon name="box" size={21} /></span><div><h2 className="font-black text-ink-950">فضای ذخیره‌سازی کاتالوگ</h2><p className="mt-1 max-w-2xl text-xs leading-6 text-slate-500">فایل PDF و تصویر کاتالوگ همه شرکت‌ها در محل انتخاب‌شده آپلود می‌شود. کلیدهای آروان پس از ذخیره نمایش داده نمی‌شوند.</p></div></div>
          <span className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-xs font-black ${storage.provider === 'Arvan' && storageConfigured && !storageLoading ? 'bg-emerald-100 text-emerald-700' : storage.provider === 'Arvan' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{storage.provider === 'Arvan' ? storageConfigured ? 'آروان‌کلاد آماده' : 'آروان نیازمند تکمیل' : 'فضای محلی'}</span>
        </div>
        {storageLoading ? <div className="h-72 animate-pulse bg-slate-50" /> : <div className="space-y-5 p-5 sm:p-7">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm font-black text-slate-700"><span>نوع فضای ذخیره‌سازی</span><select className={fieldClass} value={storage.provider} onChange={(event) => updateStorage('provider', event.target.value as StorageForm['provider'])}><option value="Local">فضای محلی سرور</option><option value="Arvan">آروان‌کلاد (Object Storage)</option></select></label>
            <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-xs leading-6 text-amber-800"><p className="font-black">نکته امنیتی</p><p className="mt-1">کلیدها فقط برای اتصال به آروان استفاده می‌شوند، رمزنگاری‌شده در سرور نگه‌داری می‌شوند و در این صفحه قابل بازیابی نیستند.</p></div>
          </div>

          {storage.provider === 'Arvan' ? <>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2 text-sm font-black text-slate-700"><span>Endpoint آروان‌کلاد</span><input className={fieldClass} dir="ltr" onChange={(event) => updateStorage('endpoint', event.target.value)} placeholder="https://s3.ir-thr-at1.arvanstorage.ir" required type="url" value={storage.endpoint} /></label>
              <label className="space-y-2 text-sm font-black text-slate-700"><span>Region</span><input className={fieldClass} dir="ltr" onChange={(event) => updateStorage('region', event.target.value)} placeholder="ir-thr-at1" required value={storage.region} /></label>
              <label className="space-y-2 text-sm font-black text-slate-700"><span>نام Bucket</span><input className={fieldClass} dir="ltr" onChange={(event) => updateStorage('bucketName', event.target.value)} placeholder="your-bucket-name" required value={storage.bucketName} /></label>
              <label className="space-y-2 text-sm font-black text-slate-700"><span>پیشوند مسیر فایل‌ها</span><input className={fieldClass} dir="ltr" onChange={(event) => updateStorage('prefix', event.target.value)} placeholder="crm/catalog" required value={storage.prefix} /></label>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2 text-sm font-black text-slate-700"><span>Access Key {hasAccessKey ? <small className="mr-1 text-emerald-600">(ثبت شده)</small> : null}</span><input className={fieldClass} dir="ltr" onChange={(event) => { setAccessKey(event.target.value); setClearAccessKey(false); }} placeholder={hasAccessKey ? 'برای حفظ کلید، خالی بگذارید' : 'Access Key'} type="password" value={accessKey} /></label>
              <label className="space-y-2 text-sm font-black text-slate-700"><span>Secret Key {hasSecretKey ? <small className="mr-1 text-emerald-600">(ثبت شده)</small> : null}</span><input className={fieldClass} dir="ltr" onChange={(event) => { setSecretKey(event.target.value); setClearSecretKey(false); }} placeholder={hasSecretKey ? 'برای حفظ کلید، خالی بگذارید' : 'Secret Key'} type="password" value={secretKey} /></label>
            </div>
            <div className="flex flex-wrap gap-4 text-xs font-bold text-slate-500"><label className="flex items-center gap-2"><input checked={clearAccessKey} className="size-4 accent-rose-600" disabled={!hasAccessKey} onChange={(event) => { setClearAccessKey(event.target.checked); if (event.target.checked) setAccessKey(''); }} type="checkbox" />حذف Access Key ثبت‌شده</label><label className="flex items-center gap-2"><input checked={clearSecretKey} className="size-4 accent-rose-600" disabled={!hasSecretKey} onChange={(event) => { setClearSecretKey(event.target.checked); if (event.target.checked) setSecretKey(''); }} type="checkbox" />حذف Secret Key ثبت‌شده</label></div>
            <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end"><label className="space-y-2 text-sm font-black text-slate-700"><span>نشانی عمومی فایل‌ها <small className="font-bold text-slate-400">(اختیاری؛ برای CDN یا دامنه اختصاصی)</small></span><input className={fieldClass} dir="ltr" onChange={(event) => updateStorage('publicBaseUrl', event.target.value)} placeholder="https://bucket.arvanstorage.ir" type="url" value={storage.publicBaseUrl} /></label><label className="flex h-11 items-center gap-3 rounded-xl border border-sky-100 bg-sky-50 px-4 text-xs font-black text-sky-800"><input checked={storage.publicRead} className="size-4 accent-sky-600" onChange={(event) => updateStorage('publicRead', event.target.checked)} type="checkbox" />دسترسی عمومی فایل‌ها</label></div>
          </> : <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-7 text-slate-600">فایل‌ها روی همان سروری که CRM اجرا شده در مسیر کاتالوگ نگه‌داری می‌شوند. برای ذخیره‌سازی امن‌تر و مستقل از سرور، آروان‌کلاد را انتخاب و اطلاعات اتصال را وارد کنید.</div>}
        </div>}
        <div className="flex flex-col-reverse justify-end gap-3 border-t border-slate-100 bg-slate-50/70 p-5 sm:flex-row sm:px-7"><button className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-sky-200 bg-white px-5 py-3 text-sm font-black text-sky-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto" disabled={storage.provider !== 'Arvan' || storageLoading || savingStorage || testingStorage} onClick={() => void testStorageConnection()} type="button"><AppIcon name="activity" size={17} />{testingStorage ? 'در حال تست اتصال…' : 'تست اتصال آروان‌کلاد'}</button><button className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-700 px-5 py-3 text-sm font-black text-white disabled:opacity-60 sm:w-auto" disabled={storageLoading || savingStorage || testingStorage} type="submit"><AppIcon name="check" size={17} />{savingStorage ? 'در حال ذخیره…' : 'ذخیره تنظیمات فضای ذخیره‌سازی'}</button></div>
      </form> : null}

      {canManageAlerts ? <form className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm" onSubmit={(event) => void saveAlerts(event)}>
        <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:p-7">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3"><span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-brand-50 text-brand-600"><AppIcon name="settings" size={20} /></span><div><h2 className="font-black text-ink-950">موتور هشدار سراسری</h2><p className="mt-1 max-w-2xl text-xs leading-6 text-slate-400">ابتدا مقدار پیش‌فرض را تعیین کنید؛ سپس در صورت نیاز برای هر نوع کسب‌وکار و قالب پیشنهادی آن، آستانه اختصاصی ثبت کنید.</p></div></div>
            <label className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3 text-sm font-black text-slate-700"><input checked={alertSettings.isEnabled} className="size-5 accent-brand-600" onChange={(event) => updateAlert('isEnabled', event.target.checked)} type="checkbox" />{alertScope === 'global' ? 'فعال برای همه' : 'فعال برای این قالب'}</label>
          </div>
          <div className="grid gap-3 rounded-2xl border border-brand-100 bg-brand-50/40 p-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <label className="space-y-2 text-sm font-black text-slate-700"><span>دامنه تنظیم هشدار</span><select className={fieldClass} disabled={alertsLoading} onChange={(event) => selectAlertScope(event.target.value)} value={alertScope}><option value="global">پیش‌فرض همه شرکت‌ها</option>{businessTypes.map((type) => <option key={type.id} value={type.id}>{type.name} — قالب {type.name}</option>)}</select></label>
            {alertScope !== 'global' ? <div className="rounded-xl bg-white px-4 py-3 text-xs leading-6 text-slate-600"><p className="font-black text-brand-700">{selectedBusinessType?.name ?? 'نوع کسب‌وکار'}</p><p>قالب پیشنهادی: {selectedProfile?.templateName ?? `قالب ${selectedBusinessType?.name ?? ''}`}</p></div> : <div className="rounded-xl bg-white px-4 py-3 text-xs leading-6 text-slate-600">این مقدار برای همه نوع‌هایی که تنظیم اختصاصی ندارند استفاده می‌شود.</div>}
          </div>
          {alertScope !== 'global' ? <div className={`flex flex-col gap-3 rounded-2xl border p-4 text-xs leading-6 sm:flex-row sm:items-center sm:justify-between ${selectedProfile ? 'border-emerald-100 bg-emerald-50/60 text-emerald-800' : 'border-amber-100 bg-amber-50/70 text-amber-800'}`}><p>{selectedProfile ? 'این نوع کسب‌وکار تنظیم هشدار اختصاصی دارد.' : 'این نوع هنوز از مقدار پیش‌فرض استفاده می‌کند؛ با ذخیره تغییرات، تنظیم اختصاصی ساخته می‌شود.'}</p>{selectedProfile ? <button className="rounded-lg border border-emerald-200 bg-white px-3 py-2 font-black text-emerald-700 disabled:opacity-50" disabled={savingAlerts} onClick={() => void resetBusinessTypeAlerts()} type="button">بازگشت به پیش‌فرض</button> : null}</div> : null}
        </div>
        {alertsLoading ? <div className="h-72 animate-pulse bg-slate-50" /> : <div className="grid gap-4 p-5 sm:p-7 md:grid-cols-3">
          <article className="rounded-2xl border border-violet-100 bg-violet-50/40 p-4"><label className="flex items-center justify-between gap-3 text-sm font-black text-slate-800">اعتبار پیامک<input checked={alertSettings.smsCreditWarningEnabled} className="size-4 accent-violet-600" onChange={(event) => updateAlert('smsCreditWarningEnabled', event.target.checked)} type="checkbox" /></label><p className="mt-2 text-xs leading-6 text-slate-500">وقتی اعتبار پیامک شرکت‌های این دامنه به این مقدار یا کمتر برسد.</p><input className="mt-4 w-full rounded-xl border border-violet-100 bg-white px-3 py-2.5 text-sm outline-none focus:border-violet-500" disabled={!alertSettings.smsCreditWarningEnabled} max={1000000} min={0} onChange={(event) => updateAlert('smsRemainingThreshold', Number(event.target.value) || 0)} type="number" value={alertSettings.smsRemainingThreshold} /><p className="mt-2 text-[11px] font-bold text-violet-700">اتمام کامل اعتبار، هشدار جداگانه دارد.</p></article>
          <article className="rounded-2xl border border-amber-100 bg-amber-50/40 p-4"><label className="flex items-center justify-between gap-3 text-sm font-black text-slate-800">تعداد پرسنل<input checked={alertSettings.staffWarningEnabled} className="size-4 accent-amber-600" onChange={(event) => updateAlert('staffWarningEnabled', event.target.checked)} type="checkbox" /></label><p className="mt-2 text-xs leading-6 text-slate-500">وقتی تعداد پرسنل فعال شرکت‌های این دامنه به این عدد یا بیشتر برسد.</p><input className="mt-4 w-full rounded-xl border border-amber-100 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-500" disabled={!alertSettings.staffWarningEnabled} max={100000} min={0} onChange={(event) => updateAlert('staffCountThreshold', Number(event.target.value) || 0)} type="number" value={alertSettings.staffCountThreshold} /></article>
          <article className="rounded-2xl border border-rose-100 bg-rose-50/40 p-4"><label className="flex items-center justify-between gap-3 text-sm font-black text-slate-800">انقضای لایسنس<input checked={alertSettings.licenseWarningEnabled} className="size-4 accent-rose-600" onChange={(event) => updateAlert('licenseWarningEnabled', event.target.checked)} type="checkbox" /></label><p className="mt-2 text-xs leading-6 text-slate-500">تعداد روزهای باقی‌مانده تا انقضای لایسنس برای شروع هشدار.</p><input className="mt-4 w-full rounded-xl border border-rose-100 bg-white px-3 py-2.5 text-sm outline-none focus:border-rose-500" disabled={!alertSettings.licenseWarningEnabled} max={365} min={0} onChange={(event) => updateAlert('licenseDaysBeforeExpiry', Number(event.target.value) || 0)} type="number" value={alertSettings.licenseDaysBeforeExpiry} /><p className="mt-2 text-[11px] font-bold text-rose-700">انقضای کامل لایسنس، هشدار جداگانه دارد.</p></article>
        </div>}
        <div className="flex justify-end border-t border-slate-100 bg-slate-50/70 p-5 sm:px-7"><button className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-5 py-3 text-sm font-black text-white disabled:opacity-60 sm:w-auto" disabled={alertsLoading || savingAlerts} type="submit"><AppIcon name="check" size={17} />{savingAlerts ? 'در حال ذخیره…' : alertScope === 'global' ? 'ذخیره پیش‌فرض هشدار' : 'ذخیره هشدار این قالب'}</button></div>
      </form> : null}
    </div>
  </main>;
};
